import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';

import { BehaviorSubject, Observable, catchError, firstValueFrom, map, of } from 'rxjs';
import { read, WorkBook, utils, ParsingOptions } from 'xlsx';

import { IHttpOption, IHttpResult, RunTimeDataService } from '@leandredev/common_ng_14';
import * as dcbm from '@leandredev/dcbm-ng-lib';
import { interfaces as I } from '@leandredev/dcbm-common-lib';
import { environment } from 'src/environments/environment';
import { ApiService } from '../api/api.service';
import {
  IDataAnalyseResult,
  SHEET_NAME,
  IRow,
  IRowGal,
  I_Sheets,
  hasAllRequiredMonthColname,
  hasAllRequiredYearColname,
  hasAllRequiredLocationColname,
  hasAllRequiredGalleryColname,
  IUploadLoctionResult,
  IRentYearlyRepportResult,
  IRentMonthlyRepportResult
} from './utils';

@Injectable({ providedIn: 'root' })
export class ReportImportService implements Partial<dcbm.service.Reportsimport> {
  private service: dcbm.service.Reportsimport;

  public sendMonthlyReportUrl: string;
  public uploadRepportsProgressUrl: string;
  public uploadAvalaibleImportUrl: string;

  constructor(private http: HttpClient, private RunTimeDataService: RunTimeDataService, private apiService: ApiService) {
    this.service = new dcbm.service.Reportsimport(this.http, true);
    this.service.urlCollection = `${environment.data.api}importexport/`;

    this.sendMonthlyReportUrl = this.service.urlCollection.concat('upload/monthlyreports');
    this.uploadRepportsProgressUrl = this.service.urlCollection.concat('upload/progress');
    this.uploadAvalaibleImportUrl = this.service.urlCollection.concat('upload/avalaible');
  }

  // API methods =======================================================================================================

  public monthlyreports<T extends I.IAct, U extends I.IFile>(
    body: U,
    query: any,
    monthsSheetsNames: string,
    options?: IHttpOption
  ): Observable<IHttpResult<T>> {
    return this.service.monthlyreports(body, query, monthsSheetsNames, options);
  }

  // ===================================================================================================================

  public analyseGalleryReportData(file: File): Promise<IDataAnalyseResult> {
    const inFile: string[] = [];
    const missingColumns = [];

    return this.readFileContent(file)
      .then((wb: WorkBook) => {
        const colName = utils
          .sheet_to_csv(wb.Sheets[SHEET_NAME.gallery])
          .split('\n')[0]
          .split(',')
          .map((t) => t.trim());

        missingColumns.push(...hasAllRequiredGalleryColname(colName));

        inFile.push(
          ...utils
            .sheet_to_json<IRowGal>(wb.Sheets[SHEET_NAME.gallery])
            .filter((r) => r.n_lolita)
            .map((r) => String(r.denomination))
        );
      })
      .then(() =>
        firstValueFrom(
          this.apiService.collections.Act.get(`templateReference/${environment.data.templates.galleryTemplate}/=/`).pipe(
            dcbm.ApiHelper.resultToArr('Aucun dossier trouvé')
          )
        )
      )
      .then((acts) => {
        const inDb = acts.map((a) => String(a.name));
        const toCreate = inFile.filter((x) => !inDb.includes(String(x)));
        const toUpdate = inFile.filter((x) => inDb.includes(String(x)));

        return { inFile, toCreate, toUpdate, inDb, missingColumns };
      });
  }

  public analyseLocationReportData(file: File): Promise<IDataAnalyseResult> {
    const inFile: string[] = [];
    const missingColumns = [];

    return this.readFileContent(file)
      .then((wb: WorkBook) => {
        const colName = utils
          .sheet_to_csv(wb.Sheets[SHEET_NAME.location])
          .split('\n')[0]
          .split(',')
          .map((t) => t.trim());

        missingColumns.push(...hasAllRequiredLocationColname(colName));

        inFile.push(
          ...utils
            .sheet_to_json<IRow>(wb.Sheets[SHEET_NAME.location])
            .filter((r) => r.n_loc && r.n_prop)
            .map((r) => String(r.n_loc))
        );
      })
      .then(() =>
        firstValueFrom(
          this.apiService.collections.Act.get(`templateReference/${environment.data.templates.reportTemplate}/=/`).pipe(
            dcbm.ApiHelper.resultToArr('Aucun dossier trouvé')
          )
        )
      )
      .then((acts) => {
        const inDb = acts.map((a) => a.name.toString());
        const toCreate = inFile.filter((x) => !inDb.includes(x));
        const toUpdate = inDb.filter((x) => inFile.includes(x));

        return { inFile, toCreate, toUpdate, inDb, missingColumns };
      });
  }

  public analyseMonthlyReportData(file: File): Promise<I_Sheets[]> {
    return this.readFileContent(file).then((wb: WorkBook) => {
      const sheetNames = wb.SheetNames.filter((n) => n.match(/[0-9]/g)).filter((y) => y.length == 6);

      const sheets: I_Sheets[] = sheetNames.map((name) => {
        return { name: name, value: wb.Sheets[name], missingColums: [] };
      });

      sheets.forEach((sheet) => {
        const colName = utils
          .sheet_to_csv(sheet.value)
          .split('\n')[0]
          .split(',')
          .map((t) => t.trim());

        sheet.missingColums = hasAllRequiredMonthColname(colName);
      });

      return sheets;
    });
  }

  public analyseYearReportData(file: File): Promise<I_Sheets[]> {
    return this.readFileContent(file).then((wb: WorkBook) => {
      const sheetNames = wb.SheetNames.filter((n) => n.match(/[0-9]/g)).filter((y) => y.length == 4);

      const sheets: I_Sheets[] = sheetNames.map((name) => {
        return { name: name, value: wb.Sheets[name], missingColums: [] };
      });

      sheets.forEach((sheet) => {
        const colName = utils
          .sheet_to_csv(sheet.value)
          .split('\n')[0]
          .split(',')
          .map((t) => t.trim());

        sheet.missingColums = hasAllRequiredYearColname(colName);
      });

      return sheets;
    });
  }

  public sendMonthlyReportToServer(file: File, months: string[]): Observable<HttpEvent<any>> {
    void this.startListeningProgress();

    const formData = new FormData();

    formData.append('file', file);
    formData.set('monthsSheetsNames', months.join(','));

    return this.http
      .post<IRentMonthlyRepportResult>(this.sendMonthlyReportUrl, formData, {
        reportProgress: true,
        observe: 'events',
        headers: this.buildHeaderWithToken()
      })
      .pipe(
        map((response) => {
          if (response.type === HttpEventType.Response) {
            const data: IRentMonthlyRepportResult = response.body;
            const msg = `${data.rentrepports.length} rapports creés et ${data.actsForUpdate.length} Dossier on éte mis a jour`;
            this.endListeningProgress(msg);
          }

          return response;
        })
      );
  }

  public sendYearlyReportToServer(file: File, years: string[]): Observable<HttpEvent<any>> {
    void this.startListeningProgress();

    const formData = new FormData();

    formData.append('file', file);
    formData.set('years', years.join(','));

    return this.http
      .post<IRentYearlyRepportResult>(this.service.urlCollection.concat('upload/yearlyreports'), formData, {
        reportProgress: true,
        observe: 'events',
        headers: this.buildHeaderWithToken()
      })
      .pipe(
        map((response) => {
          if (response.type === HttpEventType.Response) {
            const data: IRentYearlyRepportResult = response.body;
            const msg = `${data.rentrepports.length} rapports creés et ${data.actsForUpdate.length} Dossier on éte mis a jour`;
            this.endListeningProgress(msg);
          }

          return response;
        })
      );
  }

  public sendLocationsReportToServer(file: File): Observable<HttpEvent<any>> {
    void this.startListeningProgress();

    const formData = new FormData();

    formData.append('file', file);
    formData.set('locationtemplateId', environment.data.templates.reportTemplate);
    formData.set('repportActType', environment.data.defaultTypes.report.id);
    formData.set('repportActservice', environment.data.dcbmservices.report);
    formData.set('galleriestemplateId', environment.data.templates.galleryTemplate);

    return this.http
      .post<IUploadLoctionResult>(this.service.urlCollection.concat('upload/locations'), formData, {
        reportProgress: true,
        observe: 'events',
        headers: this.buildHeaderWithToken()
      })
      .pipe(
        map((response) => {
          if (response.type === HttpEventType.Response) {
            const data: IUploadLoctionResult = response.body;
            const msg = `${data.toCreate.length} dossiers de locations ajoutés et ${data.toUpdate.length} mis à jour. (${data.errors.length} erreurs)`;
            this.endListeningProgress(msg);
          }

          return response;
        })
      );
  }

  public sendGallerieReportToServer(file: File): Observable<HttpEvent<any>> {
    void this.startListeningProgress();

    const formData = new FormData();

    formData.append('file', file);
    formData.set('galleriestemplateId', environment.data.templates.galleryTemplate);
    formData.set('galleriesActType', environment.data.defaultTypes.gallery.id);
    formData.set('galleriesActservice', environment.data.dcbmservices.report);
    formData.set('locationtemplateId', environment.data.templates.reportTemplate);

    return this.http
      .post<IUploadLoctionResult>(this.service.urlCollection.concat('upload/galleries'), formData, {
        reportProgress: true,
        observe: 'events',
        headers: this.buildHeaderWithToken()
      })
      .pipe(
        map((response) => {
          if (response.type === HttpEventType.Response) {
            const data: IUploadLoctionResult = response.body;
            const msg = `${data.toCreate.length} dossiers de galeries ajoutés et ${data.toUpdate.length} mis à jour. (${data.errors.length} erreurs)`;
            this.endListeningProgress(msg);
          }

          return response;
        })
      );
  }

  private readFileContent(file: File, options?: ParsingOptions): Promise<WorkBook> {
    const rawOption: ParsingOptions = { type: 'binary' };
    const _options: ParsingOptions = options ? Object.assign(options, rawOption) : rawOption;

    return new Promise<WorkBook>((resolve, reject) => {
      if (!file) reject(Error('Données erronées'));

      const reader = new FileReader();

      reader.onload = (progressEvent: ProgressEvent<FileReader>) => {
        const ab = progressEvent.target.result;
        const wb: WorkBook = read(ab, _options);

        resolve(wb);
      };

      reader.readAsArrayBuffer(file);
    });
  }

  private buildHeaderWithToken(): HttpHeaders {
    try {
      const token = (this.RunTimeDataService.getStore('token') as { token: string }).token;
      const header: HttpHeaders = new HttpHeaders().set('jwt', token);
      return header;
    } catch (err) {
      console.warn(err);
      return null;
    }
  }

  public getAuthHeader(): { jwt: string } {
    try {
      const token = (this.RunTimeDataService.getStore('token') as { token: string }).token;
      return { jwt: token };
    } catch (err) {
      console.warn(err);
      return null;
    }
  }

  public progressSource: EventSource | undefined;
  public progressResult: BehaviorSubject<string> = new BehaviorSubject('');

  public startListeningProgress(): void {
    if (this.progressSource) return;

    this.progressResult.next('');
    this.progressSource = new EventSource(addRandomURLParams(this.uploadRepportsProgressUrl));
  }

  public endListeningProgress(message: string): void {
    setTimeout(() => {
      if (this.progressSource) {
        this.progressSource.close();
        this.progressSource = null;
      }
    }, 500);

    this.progressResult.next(message);
  }

  public refreshListetingProcess(): void {
    if (this.progressSource) {
      this.progressSource.close();
      this.progressSource = null;
    }

    this.startListeningProgress();
  }

  public isImportAvalaible(): Observable<boolean> {
    return this.http.get(addRandomURLParams(this.uploadAvalaibleImportUrl), { responseType: 'text' }).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  public isListingProgress(): boolean {
    return !!this.progressSource;
  }
}

function addRandomURLParams(str: string): string {
  return str.concat('?').concat(Math.trunc(Math.random() * 1000000).toString());
}
